// ─── 植物养成系统类型定义 ───

/** 旧版 9 种植物 ID 保留用于读取已经保存的学习者数据。 */
export type LegacyPlantSpecies =
  | 'oak' | 'pine' | 'willow' | 'maple' | 'cherry'     // 5 trees
  | 'rose' | 'lily' | 'sunflower' | 'lavender'          // 4 flowers

/** DailyLikeTrees 原始 37 种树木精灵。 */
export type DailyTreeSpecies =
  | 'tree1' | 'tree2' | 'tree3' | 'tree4' | 'tree5' | 'tree6' | 'tree7' | 'tree8' | 'tree9'
  | 'tree10' | 'tree11' | 'tree12' | 'tree13' | 'tree14' | 'tree15' | 'tree16' | 'tree17' | 'tree18' | 'tree19'
  | 'tree20' | 'tree21' | 'tree22' | 'tree23' | 'tree24' | 'tree25' | 'tree26' | 'tree27' | 'tree28' | 'tree29'
  | 'tree30' | 'tree31' | 'tree32' | 'tree33' | 'tree34' | 'tree35' | 'tree36' | 'tree37'

export type PlantSpecies = LegacyPlantSpecies | DailyTreeSpecies

export type PlantCategory = 'tree' | 'flower'

/** 生长阶段 */
export type GrowthStage = 'seedling' | 'growing' | 'mature'

export type PlantHealthStatus = 'healthy' | 'thirsty' | 'wilted' | 'dead'

export type PlantEndReason = 'matured' | 'dead'

/** 生长阶段阈值 */
export const PLANT_EXP_THRESHOLD = {
  /** 幼苗 → 中成株 */
  GROWING: 300,
  /** 中成株 → 成熟植株 */
  MATURE: 800,
} as const

/** 5大植物分区（四维 + 词汇） */
export type GardenZone = 'listening' | 'speaking' | 'reading' | 'writing' | 'vocabulary'

export const GARDEN_ZONES: GardenZone[] = ['listening', 'speaking', 'reading', 'writing', 'vocabulary']

/** 分区中文标签 */
export const GARDEN_ZONE_LABELS: Record<GardenZone, string> = {
  listening: '听力花园',
  speaking: '口语花园',
  reading: '阅读花园',
  writing: '写作花园',
  vocabulary: '词汇花园',
}

/** 分区emoji */
export const GARDEN_ZONE_EMOJI: Record<GardenZone, string> = {
  listening: '🎧',
  speaking: '🎙️',
  reading: '📖',
  writing: '✏️',
  vocabulary: '📝',
}

/** 植物配置（只读） */
export interface PlantConfig {
  id: PlantSpecies
  name: string
  nameEn: string
  category: PlantCategory
  description: string
  emoji: string
  /** 各阶段颜色 */
  colors: {
    seedling: string
    growing: string
    mature: string
    trunk: string
    flower?: string
  }
}

/** 单个分区的植物状态 */
export interface PlantState {
  /** 当前种植的植物品种，null表示未种植 */
  species: PlantSpecies | null
  /** 当前经验值 */
  exp: number
  /** 当前植物实例的生长 EXP；归档后从 0 开始，不能替代永久 EXP。 */
  growthExp: number
  /** 当前生长阶段（根据EXP计算） */
  stage: GrowthStage
  /** 种植时间 */
  plantedAt: string | null
  /** 当前植物实例身份；旧数据读取时会生成稳定兼容 ID。 */
  instanceId: string | null
  /** lastNourishedAt 时刻的养料基数，读取时按时间纯投影。 */
  nutrient: number
  /** 最近一次有效题目补养时间；null 表示旧植物尚未启用衰减。 */
  lastNourishedAt: string | null
}

/** 已结束植物实例。枯死和成熟只结束实例，不删除永久学习成果。 */
export interface PlantHistoryEntry {
  instanceId: string
  zone: GardenZone
  species: PlantSpecies
  plantedAt: string
  endedAt: string
  endReason: PlantEndReason
  finalGrowthExp: number
}

/** 图鉴条目 */
export interface AlbumEntry {
  species: PlantSpecies
  /** 首次解锁时间 */
  unlockedAt: string
  /** 成熟次数 */
  matureCount: number
  /** 最近种植时间 */
  lastPlantedAt: string
}

/** 植物花园完整状态 */
export interface PlantGardenState {
  gardens: Record<GardenZone, PlantState>
  album: Partial<Record<PlantSpecies, AlbumEntry>>
  history: PlantHistoryEntry[]
  /** Reward claim IDs already consumed by the plant experience sink. */
  processedRewardClaims: Record<string, string>
  /** Stable formative-question source IDs already consumed as nourishment. */
  processedNourishmentEvents: Record<string, string>
}

// ─── 模块 → 分区映射 ───

/**
 * 将专项训练模块映射到对应的植物分区。
 * listen→listening, speak→speaking, read→reading, write→writing, vocab→vocabulary
 */
export function practiceModuleToGardenZone(module: string): GardenZone | null {
  switch (module) {
    case 'listen': return 'listening'
    case 'speak': return 'speaking'
    case 'read': return 'reading'
    case 'write': return 'writing'
    case 'vocab': return 'vocabulary'
    default: return null
  }
}
